<template>
    <div :class="customer">
        <div class="main-container">
            <div class="toolbar">
                <img class="logo" :src="logo.image" :alt="logo.alt" transition="scale-transition" height="20px" />
            </div>
            <div class="login-form">
                <v-form class="flex-grow-1" autocomplete="on" @submit.prevent="login">
                    <v-container class="inner-container">
                        <v-row dense>
                            <v-col>
                                <h4>{{ $t('login') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('username')">
                                    <v-text-field
                                        v-model="username"
                                        dense
                                        outlined
                                        :placeholder="$t('zb') + ' meierh'"
                                        autocomplete="on"
                                        :autofocus="true"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('pw')">
                                    <v-text-field
                                        v-model="password"
                                        placeholder="..."
                                        outlined
                                        dense
                                        :type="showPw ? 'text' : 'password'"
                                        append-icon="ic-20 ic-preview ic-col-primary"
                                        @click:append="showPw = !showPw"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col class="text-right">
                                <ActionBtn :loading="loading" :disabled="loading" type="submit" @click.prevent="login">
                                    <KpIcon icon-cls="arrow-bigright" color="white" />
                                    <span>{{ $t('login') }}</span>
                                </ActionBtn>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <p>
                                    <router-link :to="{ name: 'reset_pw' }"> {{ $t('forgot_pw') }} </router-link><br />
                                </p>
                                <p>
                                    <a :href="signupUrl" rel="noopener noreferrer">{{ $t('new_free_account') }}</a>
                                </p>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <v-fade-transition>
                                    <v-alert v-if="error" type="error">{{ error }}</v-alert>
                                </v-fade-transition>
                            </v-col>
                        </v-row>
                    </v-container>
                    <v-alert v-if="infoMsg" class="info-area" outlined type="info">
                        <div class="info-text" v-html="infoMsg"></div>
                    </v-alert>
                </v-form>
                <div class="footer" v-html="loginFooterHtml"></div>
            </div>
            <div class="image-area"></div>
        </div>

        <v-dialog v-model="showPwChangeDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title>{{ $t('change_pw') }}</v-card-title>
                <v-card-text>
                    <v-form ref="pwForm" v-model="pwFormValid" dense>
                        <v-text-field v-model="oldPw" type="password" :label="$t('old_pw')" dense outlined />
                        <v-text-field
                            v-model="newPw"
                            type="password"
                            :rules="getPWFieldRules()"
                            :label="$t('new_pw')"
                            dense
                            outlined
                            @input="$refs.pwForm.validate()"
                        />
                        <v-text-field
                            v-model="newPwConfirm"
                            :label="$t('confirm_pw')"
                            :rules="getPWConfirmFieldRules()"
                            type="password"
                            dense
                            outlined
                            @input="$refs.pwForm.validate()"
                        />
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <PlainBtn @click="showPwChangeDialog = false">{{ $t('cancel') }}</PlainBtn>
                    <ActionBtn :loading="loading" :disabled="!pwFormValid" @click="onChangePwBtn">{{
                        $t('absenden')
                    }}</ActionBtn>
                </v-card-actions>
                <v-fade-transition>
                    <v-card-text v-if="pwChangeError">
                        <v-alert type="error">{{ pwChangeError }}</v-alert>
                    </v-card-text>
                </v-fade-transition>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { appUrl, customer, globalParam } from '@/helpers/globals';
import { formRules, errToStr } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';
import qs from 'qs';
import { hex_sha1 } from '@appvise/digest-sha1';
import { logo, signupUrl, loginFooterHtml } from '@/customer-specific';
import sessionStorageMixin from '@/mixins/session-storage';

export default {
    name: 'LoginView',
    mixins: [sessionStorageMixin],
    data: function () {
        return {
            username: null,
            password: null,
            showPw: false,
            loading: false,
            showPwChangeDialog: false,
            oldPw: '',
            newPw: '',
            newPwConfirm: '',
            pwFormValid: false,
            pwChangeError: null,
        };
    },
    computed: {
        error() {
            return this.$store.state.user.error;
        },
        infoMsg() {
            return (globalParam('login_page.info_html') || '').trim();
        },
    },
    created() {
        this.customer = customer();
        this.logo = logo();
        this.signupUrl = signupUrl();
        this.loginFooterHtml = loginFooterHtml();
    },
    methods: {
        ...mapActions('user', ['loginTry']),
        ...mapActions(['redirectAfterLogin']),
        async login() {
            try {
                this.loading = true;
                let { user, pwChange } = await this.loginTry({ username: this.username, password: this.password });
                if (pwChange) {
                    this.loading = false;
                    this.oldPw = '';
                    this.newPw = '';
                    this.newPwConfirm = '';
                    this.pwChangeError = null;
                    this.showPwChangeDialog = true;
                } else if (user) {
                    this.processSuccessfulLogin();
                } else {
                    this.loading = false;
                }
            } catch (e) {
                this.loading = false;
                this.error = e;
            }
        },

        processSuccessfulLogin() {
            // Clear user's session storage before proceeding:
            this.clearAllSessionStorage();

            let redirect = appUrl('/');

            // use a ?redirect= url param, if given:
            if (window.location.search) {
                let params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
                if (params?.redirect) {
                    redirect = decodeURIComponent(params.redirect);
                }
            }
            document.location.href = redirect;
        },

        getPWFieldRules() {
            return [formRules.minLength(8)];
        },

        getPWConfirmFieldRules() {
            return [formRules.minLength(8), () => this.newPw === this.newPwConfirm || this.$t('pw_not_match')];
        },

        async onChangePwBtn() {
            try {
                this.loading = true;
                await api().post('/LoginLogout/changePassword', {
                    username: this.username,
                    old_password: hex_sha1(this.oldPw),
                    new_password: hex_sha1(this.newPw),
                });
                this.password = this.newPw;
                // Now, process the login again with the new password:
                await this.login();
            } catch (e) {
                this.loading = false;
                this.pwChangeError = errToStr(e);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
$breakpoint: 710px;
$formWidth: 350px;
.main-container {
    height: 100vh;
    display: grid;
    background-color: white;
    grid-template-rows: 50px 1fr;
    grid-template-columns: auto 0;
    grid-template-areas:
        'toolbar'
        'login-form';

    @media screen and (min-width: $breakpoint) {
        grid-template-columns:
            max(0, calc(($breakpoint - $formWidth) / 2)) 350px max(0, calc(($breakpoint - $formWidth) / 2))
            1fr;
        grid-template-areas:
            'toolbar toolbar toolbar toolbar'
            '. login-form . image-area';
    }
}

.toolbar {
    padding: 5px;
    display: flex;
    align-items: center;
    grid-area: toolbar;
    background-color: var(--toolbar-bg-color);

    .logo {
        margin-left: 15px;
    }
}

.login-form {
    grid-area: login-form;
    background-color: white;
    display: flex;
    flex-direction: column;

    .inner-container {
        margin-top: 175px;
        padding-left: 20px;
        padding-right: 20px;
        width: 100%;
    }

    .footer {
        width: 100%;
        padding-left: 20px;
        padding-right: 20px;
        margin-bottom: 70px;
        font-size: 0.825rem;
    }
}

.image-area {
    display: none;
    grid-area: image-area;
    @media screen and (min-width: $breakpoint) {
        display: block;
    }
}

.info-area {
    margin-left: -40px;
    margin-right: -40px;
    .info-text {
        :deep(p) {
            line-height: 1.5em;
        }
    }
}

.tgk {
    .image-area {
        background-image: url('~@/assets/tgk-login-background.png');
        background-size: cover;
        background-repeat: no-repeat;
    }
}

.saiten {
    .toolbar {
        background-color: var(--primary-color);
    }
    .image-area {
        background-image: url('~@/assets/saiten-login-background.jpg');
        background-size: cover;
        background-repeat: no-repeat;
    }
}
</style>
